import { Link, useSearchParams } from "react-router-dom";
import type { BookingStatus } from "../../types/company";
import { FINAL_STATUSES } from "../../data/company";
import { docSummary, driverName, findDriver, findListing, listingName } from "../../lib/company";
import { useCompany } from "../../lib/companyContext";
import { cn, shortDate } from "../../lib/format";
import { PageTitle, StatusBadge } from "../../components/company/ui";
import { useT } from "../../i18n";

type TabKey = "new" | "documents" | "confirmed" | "active" | "history";

const TABS: { key: TabKey; statuses: BookingStatus[] }[] = [
  { key: "new", statuses: ["pending"] },
  { key: "documents", statuses: ["documents_required"] },
  { key: "confirmed", statuses: ["confirmed"] },
  { key: "active", statuses: ["active"] },
  { key: "history", statuses: FINAL_STATUSES },
];

const ROW_COLS =
  "grid grid-cols-2 gap-x-3 gap-y-1 lg:grid-cols-[110px_minmax(0,1.2fr)_minmax(0,1.2fr)_170px_130px_120px] lg:gap-3";

export default function CompanyBookings() {
  const t = useT();
  const bk = t.company.bookings;
  const { state } = useCompany();
  const [params, setParams] = useSearchParams();
  const tab = TABS.find((t) => t.key === params.get("tab")) ?? TABS[0];

  const rows = state.bookings
    .filter((b) => tab.statuses.includes(b.status))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  return (
    <>
      <PageTitle>{bk.title}</PageTitle>

      <div className="mb-4 flex gap-1.5 overflow-x-auto [scrollbar-width:none]">
        {TABS.map((item) => {
          const count = state.bookings.filter((b) => item.statuses.includes(b.status)).length;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setParams(item.key === "new" ? {} : { tab: item.key }, { replace: true })}
              className={cn(
                "h-8.5 cursor-pointer whitespace-nowrap rounded-full border-0 px-3.5 text-[13px] font-medium",
                item === tab ? "bg-ink text-white" : "bg-white text-ink hover:bg-[#FAFAFA]",
              )}
            >
              {bk.tabs[item.key]} · {count}
            </button>
          );
        })}
      </div>

      <div className="overflow-hidden rounded-card bg-white">
        <div className={cn(ROW_COLS, "hidden border-b border-line px-4.5 py-3 text-xs text-faint lg:grid")}>
          <span>{bk.cols.reference}</span>
          <span>{bk.cols.driver}</span>
          <span>{bk.cols.vehicle}</span>
          <span>{bk.cols.dates}</span>
          <span>{bk.cols.documents}</span>
          <span>{bk.cols.status}</span>
        </div>
        {rows.map((b) => {
          const driver = findDriver(state, b.driverId);
          const docs = docSummary(driver);
          return (
            <Link
              key={b.id}
              to={`/company/bookings/${b.id}`}
              className={cn(ROW_COLS, "items-center border-b border-surface px-4.5 py-3.5 text-[13px] text-ink no-underline hover:bg-[#FAFAFA]")}
            >
              <span className="font-mono">{b.id}</span>
              <span className="font-medium">{driverName(driver)}</span>
              <span>{listingName(findListing(state, b.listingId))}</span>
              <span className="text-ink-soft">
                {shortDate(b.start)} → {shortDate(b.end)}
              </span>
              <span className={docs.className}>{docs.text}</span>
              <span>
                <StatusBadge kind="booking" status={b.status} />
              </span>
            </Link>
          );
        })}
        {rows.length === 0 && (
          <div className="px-6 py-12 text-center text-sm text-muted">{bk.empty}</div>
        )}
      </div>
    </>
  );
}
